import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

const GoogleSheet = () => {
    const [user, setUser] = useState({ id: null, fullname: "" });
    const [sheets, setSheets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [googleSheetUrl, setGoogleSheetUrl] = useState("");
    const [membersText, setMembersText] = useState("");

    const [shareInputs, setShareInputs] = useState({});

    const accessToken = useMemo(() => localStorage.getItem("accessToken"), []);
    const authHeader = useMemo(
        () => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        [accessToken]
    );

    useEffect(() => {
        const fetchMe = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/users`, {
                    headers: authHeader,
                });
                setUser({ id: res.data._id, fullname: res.data.fullname });
            } catch (e) {
                setError("Unable to fetch current user");
                console.error(e);
            }
        };
        fetchMe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchSheets = async () => {
            if (!user.id) return;
            setLoading(true);
            setError("");
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/sheet?userId=${encodeURIComponent(user.id)}`,
                    { headers: authHeader }
                );
                setSheets(res.data || []);
            } catch (e) {
                setError("Unable to fetch sheets");
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchSheets();
    }, [user.id]);

    const parseIds = (text) =>
        Array.from(
            new Set(
                (text || "")
                    .split(/[,\s]+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
            )
        );

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name || !user.id) return;
        setLoading(true);
        setError("");
        try {
            const body = {
                name,
                owner: user.id,
                description,
                googleSheetUrl,
                members: parseIds(membersText),
            };
            const res = await axios.post(`${API_BASE_URL}/api/sheet`, body, { headers: authHeader });
            setSheets((prev) => [res.data, ...prev]);
            setName("");
            setDescription("");
            setGoogleSheetUrl("");
            setMembersText("");
        } catch (e) {
            setError(
                e?.response?.data?.err || "Unable to create sheet. Check inputs."
            );
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!id) return;
        if (!window.confirm("Delete this sheet? This cannot be undone.")) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/sheet/${id}`, { headers: authHeader });
            setSheets((prev) => prev.filter((s) => s._id !== id));
        } catch (e) {
            setError("Unable to delete sheet");
            console.error(e);
        }
    };

    const handleShare = async (id) => {
        const raw = shareInputs[id] || "";
        const ids = parseIds(raw);
        if (!id || ids.length === 0) return;
        try {
            const res = await axios.put(
                `${API_BASE_URL}/api/sheet/${id}/share`,
                { userIds: ids },
                { headers: authHeader }
            );
            setSheets((prev) => prev.map((s) => (s._id === id ? res.data : s)));
            setShareInputs((prev) => ({ ...prev, [id]: "" }));
        } catch (e) {
            setError("Unable to share sheet with users");
            console.error(e);
        }
    };

    return (
        <div className="container py-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="m-0">Sheets</h2>
            </div>

            {error && (
                <div className="alert alert-danger py-2" role="alert">
                    {error}
                </div>
            )}

            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">Create New Sheet</h5>
                    <form onSubmit={handleCreate} className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Name</label>
                            <input
                                className="form-control"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Budget Q4"
                                required
                            />
                        </div>
                        <div className="col-md-8">
                            <label className="form-label">Description</label>
                            <input
                                className="form-control"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Monthly expenses and incomes"
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Google Sheet URL</label>
                            <input
                                className="form-control"
                                value={googleSheetUrl}
                                onChange={(e) => setGoogleSheetUrl(e.target.value)}
                                placeholder="https://docs.google.com/spreadsheets/d/e/.../pubhtml?widget=true&headers=false"
                            />
                            <div className="form-text">
                                Use a published/embed URL so it can display.
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="form-label">Members (IDs, comma or space separated)</label>
                            <input
                                className="form-control"
                                value={membersText}
                                onChange={(e) => setMembersText(e.target.value)}
                                placeholder="64f...a2, 65b...e8"
                            />
                        </div>
                        <div className="col-12">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading || !user.id || !name}
                            >
                                {loading ? "Creating..." : "Create Sheet"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h5 className="card-title m-0">Your Sheets</h5>
                        {loading && <span className="text-muted">Loading…</span>}
                    </div>

                    {sheets.length === 0 ? (
                        <div className="text-muted">No sheets yet.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm align-middle">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th>Members</th>
                                        <th style={{ width: 300 }}>Share</th>
                                        <th>Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sheets.map((s) => (
                                        <>
                                            <tr key={s._id}>
                                                <td>{s.name}</td>
                                                <td className="text-truncate" style={{ maxWidth: 300 }}>
                                                    {s.description || "—"}
                                                </td>
                                                <td>
                                                    <span className="badge bg-secondary">
                                                        {Array.isArray(s.members) ? s.members.length : 0}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-2">
                                                        <div className="input-group input-group-sm">
                                                            <input
                                                                className="form-control"
                                                                placeholder="Add member IDs"
                                                                value={shareInputs[s._id] || ""}
                                                                onChange={(e) =>
                                                                    setShareInputs((prev) => ({
                                                                        ...prev,
                                                                        [s._id]: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                            <button
                                                                className="btn btn-outline-primary"
                                                                onClick={() => handleShare(s._id)}
                                                            >
                                                                Share
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <button
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() => handleDelete(s._id)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {s.googleSheetUrl && (
                                                <tr>
                                                    <td colSpan={5}>
                                                        <div style={{ width: "100%" }}>
                                                            <iframe 
                                                                title={`sheet-${s._id}`}
                                                                width="100%" height="700px" 
                                                                src={s.googleSheetUrl + '?widget=true&amp;headers=false'}
                                                                loading="lazy"/>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GoogleSheet;
